import {observable, RWSService} from '@rws-framework/client';
import type {Course} from "../types/course";
import type {User} from "../components/programs/typesRaw";
import {inAdminCourseScope} from "../types/adminCourse";
import {inCoordinatorAccessScope} from "../types/coordinatorAccess";
import StorageService, { StorageServiceInstance } from "src/services/StorageService";

class JunctionUserService extends RWSService {
  static defaultEventOptions = {
    bubbles: true,
    composed: true,
    cancelable: true
  }
  @observable inDashProgress: boolean = false;
  @observable getDashboardCallbacks = [];

  constructor(
    @StorageService private storageService: StorageServiceInstance
  ) {
    super();
  }

  get apiPrefix() {
    return window.edrnaConfig.host + window.edrnaConfig.serverPrefix;
  }

  get csrfToken() {
    return Cookies.get("csrf_token");
  }

  _dispatch(type: string, detail?: object, options?: object) {
    return document.documentElement.dispatchEvent(new CustomEvent(type, Object.assign(Object.assign({
      detail
    }, JunctionUserService.defaultEventOptions), options)));
  }

  getUser(): Promise<any> {
    return new Promise((resolve) => {
      this._dispatch('jnct:junctionuser:getuser', (data: any) => {
        resolve(data)
      })
    })
  };

  getUsersById(ids: string[]) : Promise<Array<{"_id": string, "name": string, "surname": string, "thumbnail_url": string}>> {
    // get_users
    return fetch(`${this.apiPrefix}get_users/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
      },
      body: JSON.stringify(ids),
    })
      .then((res) => res.json())
  }

  isAdmin(user: User): boolean {
    return (user.accountType === "Junction");
  };

  /** Sees the reseller-wide Learnosity item bank rather than only the course's own precedents. */
  isResellerAdmin(user: User): boolean {
    return this.isSuperAdmin(user) || user.admin_role_permissions.course_content === "rw";
  };

  isSuperAdmin(user: User): boolean {
    return user.permissions.includes("admin");
  }

  hasCourseContentRights(course: Course, user: User): boolean {
    return user.admin_role_permissions?.course_content === "rw"
      && inAdminCourseScope(course?._id, user.admin_course);
  };

  hasCoordinatorAccess(course: Course, user: User): boolean {
    return inCoordinatorAccessScope(course?._id, user.coordinator_access);
  };

  _profRights(course: Course, user: User, mustHaveEditRights: boolean) {
    if (!course || !user) return false;
    if (this.isAdmin(user) || this.hasCourseContentRights(course, user) || this.hasCoordinatorAccess(course, user)) return true;

    if ((course.instructors||[]).length && course.instructors.includes(user.mongoId)) {
      return true;
    }
    if (!mustHaveEditRights && (course.assistants||[]).length && course.assistants.includes(user.mongoId)) {
      return true;
    }

    return false;
  };

  hasProfessorRights (course: Course, user: User) {
    return this._profRights(course, user, false);
  };

  hasProfessorEditRights (course: Course, user: User) {
    return this._profRights(course, user, true);
  };

  async getDashboard() {
    const dashboard = this.storageService.get("prop", "dashboard");
    if (dashboard) {
      return Promise.resolve(dashboard);
    }

    if (this.inDashProgress) {
      return new Promise((resolve, reject) => {
          this.getDashboardCallbacks.push({ resolve, reject });
      });
    }

    this.inDashProgress = true;
    try {
      const csrfToken = Cookies.get('csrf_token');
      const APIprefix = window.edrnaConfig.host + window.edrnaConfig.serverPrefix;

      // student_dashboard
      const response = await fetch(`${APIprefix}dashboard/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      this.inDashProgress = false;
      this.getDashboardCallbacks.forEach(({ resolve }) => resolve(data));
      this.storageService.set("prop", "dashboard", data);
      return data;
    } catch (error) {
      this.inDashProgress = false;
      this.getDashboardCallbacks.forEach(({ reject }) => reject(error));
      throw error;
    }
  }
}

export default JunctionUserService.getSingleton()

export {JunctionUserService as JunctionUserServiceInstance}
